import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import ERROR_MESSAGE, { ERROR_TYPE } from '@/constants/errors.constants';
import type { ConfigTramite, FormValues, IDocumentoFormGroup, ISujetoGroup } from '@/interfaces/model/don.interface';
import { type ILookup } from '@/interfaces/model/generic.interface';
import { type TipoDocumento, type TipoIntervencion } from '@/interfaces/model/maestros.interface';
import type { Resolver } from 'react-hook-form';

import { flattenGroupSujetos } from './formSolicitudes';
import { documentoLimitadoSchema } from './validationsDocuments';

/** 
 * Fecha vencimiento es un campo que puede ser obligatorio o no según el maestro configuracionTramite.
 * FIX TEMPORAL: CTNCalendar devuelve null, al eliminar su valor, 
 * null no es un type que se trabaje en el proyecto por lo que se transforma a undefined
 */
const validateFechaVencimiento = (configuracionTramite?: ConfigTramite) =>
	yup
		.date()
		.transform((value, originalValue) => (originalValue === null ? undefined : value))
		.optional()
		.when([], {
			is: () => configuracionTramite?.permiteVencimiento === true,
			then: (schema) => schema.test(ERROR_TYPE.required, ERROR_MESSAGE.required, (value) => Boolean(value)),
			otherwise: (schema) => schema.notRequired(),
		});

/** Validación para comprobar según el maestro tipoIntervención si se encuentran añadidos todos los sujetos obligatorios */
const validateRequiredSujeto = (values: ISujetoGroup, tipoIntervencionMaestro: TipoIntervencion[]) => {
	const { items, tipoIntervencion } = values;

	const minRequired = tipoIntervencionMaestro.find(({ id }) => tipoIntervencion?.id === id)?.minimoObligatorio ?? 0;

	if (!items || items.length < minRequired) {
		return false;
	}

	return true;
};

/** Validación para comprobar según el maestro tipoDocumento si se encuentran añadidos todos los documentos obligatorios */
const validateRequiredDocumento = (values: IDocumentoFormGroup, tipoDocumentoMaestro: TipoDocumento[]) => {
	const { items, tipoDocumento } = values;

	const minRequired = tipoDocumentoMaestro.find(({ id }) => tipoDocumento?.id === id)?.minimoObligatorio ?? 0;

	if (!items || items.length < minRequired) {
		return false;
	}

	return true;
};

// Puede darse el caso que exista un trámite sin sujetos requeridos
// Entonces deberá informarse almenos un interviniente adicional
const validateMinInterviniente = (values: FormValues, context: yup.TestContext<yup.AnyObject>) => {
	const { sujetosAdicionales, sujetosRequeridos } = values;
	const countAdicionales = sujetosAdicionales?.length ?? 0;
	const countRequeridos = flattenGroupSujetos(sujetosRequeridos).length ?? 0;

	const totalSujetos = countAdicionales + countRequeridos;

	if (totalSujetos === 0) {
		return context.createError({
			path: 'sujetosAdicionales',
			message: ERROR_MESSAGE.minInterviniente,
		});
	}

	return true;
};

export const solicitudSchema = ({
	tipoDocumentoMaestro,
	tipoIntervencionMaestro,
	configuracionTramite,
	globalValues,
}: {
	tipoDocumentoMaestro: TipoDocumento[];
	tipoIntervencionMaestro: TipoIntervencion[];
	configuracionTramite?: ConfigTramite;
	globalValues?: FormValues;
}) =>
	yup
		.object()
		.shape({
			fechaVencimiento: validateFechaVencimiento(configuracionTramite),
			numeroTramite: yup.string().required(ERROR_MESSAGE.required),
			sujetosAdicionales: yup.array().required(),
			sujetosRequeridos: yup
				.array()
				.of(
					yup
						.object({
							tipoIntervencion: yup.mixed<ILookup>().defined(),
							items: yup.array().required(),
						})
						.test(ERROR_TYPE.sujetoRequired, ERROR_MESSAGE.sujetoRequired, (values) =>
							validateRequiredSujeto(values, tipoIntervencionMaestro),
						),
				)
				.required(),
			documentosAdicionales: yup.array().required(),
			notarioSeleccionado: configuracionTramite?.permitePreseleccionNotario
				? yup.string().required(ERROR_MESSAGE.required) 
				: yup.string().optional(),
			firmaTelematica: configuracionTramite?.permitePreseleccionNotario
				? yup.boolean()
					.transform((value) => (value === null ? undefined : value))
					.test('is-req', ERROR_MESSAGE.required, (val) => val === true || val === false)
				: yup.boolean()
					.transform((value) => (value === null ? undefined : value))
					.optional(),
			documentosRequeridos: yup
				.array()
				.of(
					yup
						.object({
							tipoDocumento: yup.mixed<ILookup>().defined(),
							items: yup
								.array()
								.of(
									documentoLimitadoSchema({
										maxSizeAllDoc: configuracionTramite?.pesoMaximoSolicitud ?? 0,
										globalValues,
									}).test(ERROR_TYPE.required, ERROR_MESSAGE.docRequired, (doc) =>
										Boolean(doc.nombre && doc.nombre.trim().length > 0),
									),
								)
								.required(),
						})
						.test(ERROR_TYPE.documentoRequired, ERROR_MESSAGE.documentoRequired, (values) =>
							validateRequiredDocumento(values, tipoDocumentoMaestro),
						),
				)
				.required(),
		})
		.test(ERROR_TYPE.minInterviniente, ERROR_MESSAGE.minInterviniente, (values, context) =>
			validateMinInterviniente(values as FormValues, context),
		);

export const resolver =
	({
		tipoDocumentoMaestro,
		tipoIntervencionMaestro,
		configuracionTramite,
	}: {
		tipoDocumentoMaestro: TipoDocumento[];
		tipoIntervencionMaestro: TipoIntervencion[];
		configuracionTramite?: ConfigTramite;
	}): Resolver<FormValues, string> =>
		async (values, context, options) => {
			const schema = solicitudSchema({
				tipoDocumentoMaestro,
				tipoIntervencionMaestro,
				configuracionTramite,
				globalValues: values,
			});

			return yupResolver(schema)(values, context, options);
		};
